/* eslint-disable max-lines */
import {
  BizNestClientError,
  DatabaseAuto,
  IbulkDelete,
  IbulkDeleteSuccessResponse,
  IcommerceUser,
  IdataArrayResponse,
  IdeleteOneResponse,
  IfilterProps, IuserWallet,
  IwalletHistory,
  TuserWalletRequest,
  validateData
} from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { CommerceClient } from '../../commerce-client';

type TuserWalletProps = IuserWallet<number, IcommerceUser>;

/**
 * Represents a user's wallet in the commerce system, extending the base database auto functionality.
 * This class provides methods for managing wallet data, including balance tracking and CRUD operations.
 *
 * @extends {DatabaseAuto} Provides common database operation methods
 * @public
 * @remarks
 * This is the raw DTO shape as returned by the API. Prefer using the
 * {@link UserWallet} class when invoking client operations.
 *
 * @usage
 * ```typescript
 * const wallet = await UserWallet.getAll();
 *
 * const wallet = await UserWallet.filterAll({
 *
 * });
 *
 * const wallet = await UserWallet.bulkDelete({
 *
 * });
 *
 * const wallet = await UserWallet.getOne('resourceId');
 *
 * const wallet = await UserWallet.remove();
 *
 * ```
 */
export class UserWallet extends DatabaseAuto {
  /**
   * Base URL for wallet-related API endpoints.
   * @private @static @readonly
   */
  private static readonly baseUrl = '/wallet';

  /**
   * Unique reference identifier for the wallet.
   * @readonly
   */
  readonly urId: TuserWalletProps['urId'];

  /**
   * The commerce user associated with this wallet.
   * @readonly
   */
  readonly commerceUser: TuserWalletProps['commerceUser'];

  /**
   * Current balance in the wallet.
   * @readonly
   */
  readonly accountBalance: TuserWalletProps['accountBalance'];

  /**
   * Creates a new UserWallet instance.
   *
   * @param {TuserWalletProps} wallet - The wallet data used to initialize the instance.
   * @throws {BizNestClientError} When wallet data is invalid or missing required fields.
   * @throws {Error} When validation of wallet data fails.
   *
   * @example
   * const walletData = {
   *   urId: 'user-123',
   *   commerceUser: { /* user data *\/ },
   *   accountBalance: 100.50
   * };
   * const wallet = new UserWallet(walletData);
   */
  constructor(wallet: TuserWalletProps) {
    validateData(wallet, {
      urId: {
        validate: (v) => typeof v === 'string' && v.trim().length > 0,
        message: 'User ID must be a non-empty string'
      },
      accountBalance: {
        required: false,
        validate: (v) => v === undefined || (typeof v === 'number' && v >= 0),
        message: 'Account balance must be a non-negative number'
      }
    });
    super(wallet);
    this.urId = wallet.urId;
    this.commerceUser = wallet.commerceUser;
    this.accountBalance = wallet.accountBalance || 0;
  }

  /**
   * Retrieves a paginated list of all user wallets.
   *
   * @static
   * @param {number} [offset=0] - The number of records to skip (for pagination).
   * @param {number} [limit=20] - The maximum number of records to return.
   * @returns {Promise<{ count: number, dataArr: UserWallet[] }>}
   * An object containing the total count and an array of UserWallet instances.
   * @throws {Error} When the API request fails or returns an error.
   *
   * @example
   * // Get first page of 10 wallets
   * const { count, dataArr } = await UserWallet.getAll(0, 10);
   */
  static async getAll(offset = 0, limit = 20): Promise<{ count: number; dataArr: UserWallet[] }> {
    const query = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString()
    }).toString();
    const observer$ = CommerceClient.ehttp
      .makeGet<IdataArrayResponse<TuserWalletProps>>(`${this.baseUrl}?${query}`);
    const result = await lastValueFrom(observer$);

    return {
      count: result.count,
      dataArr: result.data.map(val => new UserWallet(val))
    };
  }

  /**
   * Retrieves a filtered list of user wallets based on the provided criteria.
   *
   * @static
   * @param {IfilterProps} filter - The filter criteria for retrieving wallets.
   * @returns {Promise<{ count: number, dataArr: UserWallet[] }>}
   * An object containing the total count and an array of filtered UserWallet instances.
   * @throws {Error} When the API request fails or returns an error.
   *
   * @example
   * const filter = {
   *   search: 'john@example.com',
   *   minBalance: 100
   * };
   * const { count, dataArr } = await UserWallet.filterAll(filter);
   */
  static async filterAll(filter: IfilterProps): Promise<{ count: number; dataArr: UserWallet[] }> {
    const observer$ = CommerceClient.ehttp
      .makePost<IdataArrayResponse<TuserWalletProps>>(`${this.baseUrl}/filter`, filter);
    const result = await lastValueFrom(observer$);

    return {
      count: result.count,
      dataArr: result.data.map(val => new UserWallet(val))
    };
  }

  /**
   * Retrieves a single wallet by its unique identifier.
   *
   * @static
   * @param {string} resourceId - The unique identifier of the wallet to retrieve.
   * @returns {Promise<UserWallet>} A UserWallet instance for the requested wallet.
   * @throws {Error} When the wallet is not found or the API request fails.
   *
   * @example
   * const wallet = await UserWallet.getOne('wallet-123');
   */
  static async getOne(resourceId: string): Promise<UserWallet> {
    const observer$ = CommerceClient.ehttp
      .makeGet<TuserWalletProps>(`${this.baseUrl}/${resourceId}`);

    return new UserWallet(await lastValueFrom(observer$));
  }

  /**
   * Creates a new wallet in the system.
   *
   * @static
   * @param {TuserWalletRequest<true>} payload - The wallet data to create.
   * @returns {Promise<UserWallet>} A new UserWallet instance for the created wallet.
   * @throws {Error} When wallet creation fails or validation errors occur.
   *
   * @example
   * const newWallet = await UserWallet.add({
   *   userId: 'user-123',
   *   accountBalance: 0,
   *   // ... other required fields
   * });
   */
  static async add(payload: TuserWalletRequest<true>): Promise<UserWallet> {
    const observer$ = CommerceClient.ehttp
      .makePost<TuserWalletProps>(`${this.baseUrl}`, payload);

    return new UserWallet(await lastValueFrom(observer$));
  }

  /**
   * Deletes multiple wallets based on the provided criteria.
   *
   * @static
   * @param {IbulkDelete} payload - The criteria for selecting wallets to delete.
   * @returns {Promise<IbulkDeleteSuccessResponse>} The result of the bulk delete operation.
   * @throws {Error} When the deletion fails or validation errors occur.
   *
   * @example
   * const result = await UserWallet.bulkDelete({
   *   ids: ['wallet-1', 'wallet-2']
   * });
   */
  static bulkDelete(payload: IbulkDelete): Promise<IbulkDeleteSuccessResponse> {
    const observer$ = CommerceClient.ehttp
      .makePost<IbulkDeleteSuccessResponse>(`${this.baseUrl}/bulk-delete`, payload);

    return lastValueFrom(observer$);
  }

  /**
   * Updates the current wallet's information.
   *
   * @param {TuserWalletRequest} payload - The updated wallet data.
   * @returns {Promise<UserWallet>} A new UserWallet instance with the updated data.
   * @throws {Error} When the update fails or validation errors occur.
   *
   * @example
   * const updatedWallet = await wallet.update({
   *   accountBalance: 150.75
   *   // ... other fields to update
   * });
   */
  async update(payload: TuserWalletRequest): Promise<UserWallet> {
    const observer$ = CommerceClient.ehttp
      .makePut<TuserWalletProps>(
        `${(this.constructor as typeof UserWallet).baseUrl}/${this._id}`,
        payload
      );

    return new UserWallet(await lastValueFrom(observer$));
  }

  /**
   * Deletes the current wallet from the system.
   *
   * @returns {Promise<IdeleteOneResponse | null>} The result of the deletion operation.
   * @throws {Error} When the deletion fails or the wallet is not found.
   *
   * @example
   * const result = await wallet.remove();
   * if (result?.success) {
   *   console.log('Wallet removed successfully');
   * }
   */
  remove(): Promise<IdeleteOneResponse | null> {
    const observer$ = CommerceClient.ehttp
      .makeDelete<IdeleteOneResponse | null>(`${(this.constructor as typeof UserWallet).baseUrl}/${this._id}`);

    return lastValueFrom(observer$);
  }
}

/**
 * Represents a wallet transaction history entry in the commerce system.
 * This class provides methods for managing wallet transaction history data.
 *
 * @extends {DatabaseAuto} Provides common database operation methods
 * @since 1.0.0
 */
export class UserWalletHistory extends DatabaseAuto {
  /**
   * Base URL for wallet history API endpoints.
   * @private @static @readonly
   */
  private static readonly baseUrl = '/wallet-history';

  /**
   * Unique reference identifier for the history entry.
   * @readonly
   */
  readonly urId: string;

  /**
   * The wallet ID or wallet object associated with this history entry.
   * @readonly
   */
  readonly wallet: string | IuserWallet;

  /**
   * The transaction amount.
   * @readonly
   */
  readonly amount: number;

  /**
   * The type of transaction (withdrawal or deposit).
   * @readonly
   */
  readonly type: 'withdrawal' | 'deposit';

  /**
   * Creates a new UserWalletHistory instance.
   *
   * @param {IwalletHistory} walletHist - The wallet history data.
   * @throws {BizNestClientError} When no wallet history data is provided.
   *
   * @example
   * const historyData = {
   *   urId: 'hist-123',
   *   wallet: 'wallet-123',
   *   amount: 50.00,
   *   type: 'deposit'
   * };
   * const history = new UserWalletHistory(historyData);
   */
  constructor(walletHist: IwalletHistory) {
    if (!walletHist) {
      throw new BizNestClientError('No data provided');
    }
    super(walletHist);
    this.urId = walletHist.urId;
    this.wallet = walletHist.wallet;
    this.amount = walletHist.amount;
    this.type = walletHist.type;
  }

  /**
   * Retrieves a paginated list of wallet history entries.
   *
   * @static
   * @param {number} [offset=0] - The number of records to skip (for pagination).
   * @param {number} [limit=20] - The maximum number of records to return.
   * @returns {Promise<{ count: number, dataArr: UserWalletHistory[] }>}
   * An object containing the total count and an array of UserWalletHistory instances.
   * @throws {Error} When the API request fails or returns an error.
   *
   * @example
   * // Get first page of 15 history entries
   * const { count, dataArr } = await UserWalletHistory.getAll(0, 15);
   */
  static async getAll(offset = 0, limit = 20): Promise<{ count: number; dataArr: UserWalletHistory[] }> {
    const query = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString()
    }).toString();
    const observer$ = CommerceClient.ehttp
      .makeGet<IdataArrayResponse<IwalletHistory>>(`${this.baseUrl}?${query}`);
    const result = await lastValueFrom(observer$);

    return {
      count: result.count,
      dataArr: result.data.map(val => new UserWalletHistory(val))
    };
  }

  /**
   * Retrieves a filtered list of wallet history entries based on the provided criteria.
   *
   * @static
   * @param {IfilterProps} filter - The filter criteria for retrieving history entries.
   * @returns {Promise<{ count: number, dataArr: UserWalletHistory[] }>}
   * An object containing the total count and an array of filtered UserWalletHistory instances.
   * @throws {Error} When the API request fails or returns an error.
   *
   * @example
   * const filter = {
   *   walletId: 'wallet-123',
   *   startDate: '2023-01-01',
   *   endDate: '2023-12-31',
   *   type: 'deposit'
   * };
   * const { count, dataArr } = await UserWalletHistory.filterAll(filter);
   */
  static async filterAll(filter: IfilterProps): Promise<{ count: number; dataArr: UserWalletHistory[] }> {
    const observer$ = CommerceClient.ehttp
      .makePost<IdataArrayResponse<IwalletHistory>>(`${this.baseUrl}/filter`, filter);
    const result = await lastValueFrom(observer$);

    return {
      count: result.count,
      dataArr: result.data.map(val => new UserWalletHistory(val))
    };
  }

  /**
   * Retrieves a single wallet history entry by its unique identifier.
   *
   * @static
   * @param {string} resourceId - The unique identifier of the history entry to retrieve.
   * @returns {Promise<UserWalletHistory>} A UserWalletHistory instance for the requested entry.
   * @throws {Error} When the history entry is not found or the API request fails.
   *
   * @example
   * const historyEntry = await UserWalletHistory.getOne('history-123');
   */
  static async getOne(resourceId: string): Promise<UserWalletHistory> {
    const observer$ = CommerceClient.ehttp
      .makeGet<IwalletHistory>(`${this.baseUrl}/${resourceId}`);

    return new UserWalletHistory(await lastValueFrom(observer$));
  }

  /**
   * Deletes multiple wallet history entries based on the provided criteria.
   *
   * @static
   * @param {IbulkDelete} payload - The criteria for selecting history entries to delete.
   * @returns {Promise<IbulkDeleteSuccessResponse>} The result of the bulk delete operation.
   * @throws {Error} When the deletion fails or validation errors occur.
   *
   * @example
   * const result = await UserWalletHistory.bulkDelete({
   *   ids: ['history-1', 'history-2']
   * });
   */
  static bulkDelete(payload: IbulkDelete): Promise<IbulkDeleteSuccessResponse> {
    const observer$ = CommerceClient.ehttp
      .makePost<IbulkDeleteSuccessResponse>(`${this.baseUrl}/bulk-delete`, payload);

    return lastValueFrom(observer$);
  }

  /**
   * Deletes the current wallet history entry from the system.
   *
   * @returns {Promise<IdeleteOneResponse | null>} The result of the deletion operation.
   * @throws {Error} When the deletion fails or the history entry is not found.
   *
   * @example
   * const result = await historyEntry.remove();
   * if (result?.success) {
   *   console.log('History entry removed successfully');
   * }
   */
  remove(): Promise<IdeleteOneResponse | null> {
    const observer$ = CommerceClient.ehttp
      .makeDelete<IdeleteOneResponse | null>(`${(this.constructor as typeof UserWalletHistory).baseUrl}/${this._id}`);

    return lastValueFrom(observer$);
  }
}
